import { GoogleAuthProvider, OAuthProvider, signInWithPopup, signInWithCustomToken } from 'firebase/auth';

const apiUrl = process.env.REACT_APP_PROJECT_ID === "shopik-production"
  ? "https://us-central1-shopik-production.cloudfunctions.net/api_handler/api/"
  : process.env.REACT_APP_IS_LOCAL == 1
    ? "http://localhost:5001/shopik-stage/us-central1/api_handler/api/"
    : "https://on-request-3lirqocyfq-uc.a.run.app/api/";

const providerFor = (mode) => {
  if (mode === 'apple') {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    return provider;
  }
  return new GoogleAuthProvider();
};

const isEmbedded = () => {
  try {
    return window.top !== window.self;
  } catch {
    // Cross-origin access to window.top throws — that itself means we're framed.
    return true;
  }
};

// Top-level tab (the normal case: a visitor on the site directly, or Shopify
// admin opening this app's login/signup page in a new tab via a plain link):
// plain signInWithPopup, same as always. (signInWithRedirect was tried here
// instead, to dodge a nested-popup-blocked issue on the Shopify path below,
// but its pending-redirect state didn't reliably survive the round trip
// through Google in this app's auth setup — it just looped. That's a
// separate problem from the popup-blocked one, so revert this path to
// popup and solve the Shopify case differently, below.)
//
// Genuinely embedded in a third-party iframe: Google refuses to render
// accounts.google.com framed (X-Frame-Options), so neither popup nor
// redirect can happen inside the iframe itself. Open the sign-in in a
// standalone tab (authPopup.js) instead, then exchange the ID token it
// hands back for a custom token via the existing /verify-auth endpoint (the
// same exchange login.js already uses for its email-link sign-in) and sign
// in this frame with that custom token.
export const signInWithProvider = (auth, mode) => {
  if (!isEmbedded()) {
    return signInWithPopup(auth, providerFor(mode));
  }

  return new Promise((resolve, reject) => {
    const popup = window.open(
      `${window.location.origin}/auth-popup?mode=${mode}`,
      'shopik-oauth-popup',
      'width=480,height=640'
    );

    if (!popup) {
      reject(new Error('Popup blocked. Please allow popups for this site and try again.'));
      return;
    }

    const cleanup = () => {
      window.removeEventListener('message', onMessage);
      clearInterval(watchdog);
    };

    const watchdog = setInterval(() => {
      if (popup.closed) {
        cleanup();
        reject(new Error('Sign-in window was closed before completing.'));
      }
    }, 500);

    const onMessage = async (event) => {
      if (event.origin !== window.location.origin || event.data?.source !== 'shopik-oauth') {
        return;
      }
      cleanup();
      if (event.data.action === 'oauthError') {
        reject(new Error(event.data.message || 'Sign-in failed.'));
        return;
      }
      if (event.data.action === 'oauthComplete') {
        try {
          const response = await fetch(`${apiUrl}verify-auth`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: event.data.idToken }),
          });
          const result = await response.json();
          if (!response.ok || !result.customToken) {
            throw new Error(result.error || 'Sign-in failed.');
          }
          resolve(await signInWithCustomToken(auth, result.customToken));
        } catch (error) {
          reject(error);
        }
      }
    };

    window.addEventListener('message', onMessage);
  });
};
